import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://predict90.com';
const SITE_NAME = 'Predict90';
const DEFAULT_DESCRIPTION = 'Predict football match scores and compete with friends in Predict90. Join the ultimate prediction competition platform.';
const DEFAULT_IMAGE = `${SITE_URL}/Predict90Vertical.png`;

/**
 * Comprehensive SEO hook for managing meta tags, OpenGraph, Twitter Cards, and canonical URLs
 * 
 * @param {Object} options - SEO configuration options
 * @param {string} options.title - Page title (will be appended with site name)
 * @param {string} options.description - Meta description (defaults to site description)
 * @param {string} options.image - OpenGraph/Twitter image URL (defaults to logo)
 * @param {string} options.url - Canonical URL (defaults to current path)
 * @param {string} options.type - OpenGraph type (defaults to 'website')
 * @param {string} options.keywords - Meta keywords (optional)
 * @param {boolean} options.noindex - Set to true to prevent indexing
 */
export const useSEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  keywords,
  noindex = false,
} = {}) => {
  const location = useLocation();
  
  useEffect(() => {
    // Get current URL
    const currentUrl = url || `${SITE_URL}${location.pathname}`;
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    
    // Update document title
    document.title = fullTitle;
    
    // Helper function to update or create meta tag
    const updateMetaTag = (name, content, attribute = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    
    // Helper function to update or create link tag
    const updateLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };
    
    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    
    // Robots meta tag
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag && robotsTag.getAttribute('content') === 'noindex, nofollow') {
        robotsTag.remove();
      }
    }
    
    // OpenGraph tags
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:description', description, 'property');
    updateMetaTag('og:image', image, 'property');
    updateMetaTag('og:url', currentUrl, 'property');
    updateMetaTag('og:type', type, 'property');
    updateMetaTag('og:site_name', SITE_NAME, 'property');
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    
    // Canonical URL
    updateLinkTag('canonical', currentUrl);
    
    // Cleanup function
    return () => {
      // Note: We don't remove meta tags on cleanup as they'll be updated by the next page
      // This prevents flickering between page transitions
    };
  }, [title, description, image, url, type, keywords, noindex, location.pathname]);
};

export default useSEO;

